import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CampaignIcon from "@mui/icons-material/Campaign";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import api from "../../services/api";
import StatsCard from "../../components/admin/StatsCard";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import EmptyState from "../../components/admin/EmptyState";
import useAuthStore from "../../store/authStore";
import { canManageDream } from "../../utils/adminAccess";

const STATUS_OPTIONS = [
  { value: "ALL", label: "전체" },
  { value: "PENDING", label: "미확인" },
  { value: "CHECKED", label: "확인완료" },
];

function CustomerVoicePage() {
  const user = useAuthStore((state) => state.user);
  const canMutate = canManageDream(user);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailVoice, setDetailVoice] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const fetchVoices = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/customer-voices");
      setVoices(res.data || []);
    } catch (err) {
      console.error("고객의 목소리 목록 조회 실패:", err);
      setVoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoices();
  }, []);

  const summary = useMemo(() => {
    const pendingCount = voices.filter((voice) => voice.status === "PENDING").length;
    const checkedCount = voices.filter((voice) => voice.status === "CHECKED").length;
    const anonymousCount = voices.filter((voice) => voice.anonymous).length;
    return {
      total: voices.length,
      pendingCount,
      checkedCount,
      anonymousCount,
    };
  }, [voices]);

  const filteredVoices = useMemo(() => {
    const query = search.trim().toLowerCase();

    return voices.filter((voice) => {
      const matchesStatus = statusFilter === "ALL" || voice.status === statusFilter;
      const matchesSearch =
        !query ||
        [voice.title, voice.content, voice.contactInfo, voice.userName]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query));

      return matchesStatus && matchesSearch;
    });
  }, [voices, search, statusFilter]);

  const handleOpenDetail = async (voiceId) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await api.get(`/api/admin/customer-voices/${voiceId}`);
      setDetailVoice(res.data);
    } catch (err) {
      console.error("고객의 목소리 상세 조회 실패:", err);
      setDetailVoice(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleMarkChecked = async () => {
    if (!detailVoice) return;

    setStatusUpdating(true);
    try {
      const res = await api.patch(
        `/api/admin/customer-voices/${detailVoice.voiceId}/status`,
        { status: "CHECKED" }
      );
      const updatedVoice = res.data;
      setDetailVoice(updatedVoice);
      setVoices((prev) =>
        prev.map((voice) =>
          voice.voiceId === updatedVoice.voiceId ? updatedVoice : voice
        )
      );
    } catch (err) {
      console.error("고객의 목소리 상태 변경 실패:", err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("ko-KR");
  };

  const getStatusChip = (status) => {
    if (status === "CHECKED") {
      return <Chip label="확인완료" color="success" size="small" />;
    }
    return <Chip label="미확인" color="warning" size="small" />;
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        들어드림 고객 목소리함
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="전체 접수"
            value={`${summary.total}건`}
            icon={CampaignIcon}
            color="#2a9ac6"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="미확인"
            value={`${summary.pendingCount}건`}
            icon={PendingActionsIcon}
            color="#f39c12"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="확인완료"
            value={`${summary.checkedCount}건`}
            icon={MarkEmailReadIcon}
            color="#27ae60"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="익명 제출"
            value={`${summary.anonymousCount}건`}
            icon={PersonOffIcon}
            color="#8e44ad"
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="제목, 내용, 연락처, 작성자 검색..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>상태</InputLabel>
              <Select
                value={statusFilter}
                label="상태"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingSkeleton variant="table" rows={6} />
      ) : filteredVoices.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState message="접수된 고객의 목소리가 없습니다." icon={CampaignIcon} />
          </CardContent>
        </Card>
      ) : (
        <Paper sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>접수일시</TableCell>
                  <TableCell>제목</TableCell>
                  <TableCell>작성자 유형</TableCell>
                  <TableCell>연락처</TableCell>
                  <TableCell>상태</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((voice) => (
                    <TableRow
                      key={voice.voiceId}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleOpenDetail(voice.voiceId)}
                    >
                      <TableCell>{formatDateTime(voice.createdAt)}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{voice.title}</TableCell>
                      <TableCell>
                        {voice.anonymous ? (
                          <Chip label="익명" size="small" variant="outlined" />
                        ) : (
                          <Chip
                            label={voice.userName || "회원"}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>{voice.contactInfo || "-"}</TableCell>
                      <TableCell>{getStatusChip(voice.status)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredVoices.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="페이지당 행"
          />
        </Paper>
      )}

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>고객의 목소리 상세</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <LoadingSkeleton variant="table" rows={4} />
          ) : !detailVoice ? (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              상세 정보를 불러오지 못했습니다.
            </Typography>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {getStatusChip(detailVoice.status)}
                {detailVoice.anonymous ? (
                  <Chip label="익명 제출" size="small" variant="outlined" />
                ) : (
                  <Chip
                    label={`회원 제출: ${detailVoice.userName || "-"}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>

              <Typography variant="subtitle2" color="text.secondary">
                제목
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {detailVoice.title}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                내용
              </Typography>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", mb: 3, lineHeight: 1.8 }}
              >
                {detailVoice.content}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    작성자 정보
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {detailVoice.anonymous
                      ? "익명 제출"
                      : `${detailVoice.userName || "-"} / ${detailVoice.userEmail || "-"} / ${
                          detailVoice.userPhoneNumber || "-"
                        }`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    선택 연락처
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {detailVoice.contactInfo || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    접수일시
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {formatDateTime(detailVoice.createdAt)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    확인 처리
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {detailVoice.checkedAt
                      ? `${formatDateTime(detailVoice.checkedAt)} / ${
                          detailVoice.checkedByName || "관리자"
                        }`
                      : "아직 확인하지 않음"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
          {canMutate && detailVoice?.status === "PENDING" && (
            <Button
              variant="contained"
              onClick={handleMarkChecked}
              disabled={statusUpdating}
            >
              {statusUpdating ? "처리 중..." : "확인완료 처리"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerVoicePage;
